import { supabase } from "@/integrations/supabase/client";

export interface CorrelationReportData {
  tickers: string[];
  matrix: number[][];
  period: string;
  dataPoints: number;
  clusters?: { members: string[]; avgIntraCorr: number }[];
  sectorMap?: Record<string, string>;
  insights?: {
    avgCorr: number;
    mostCorrelated: { a: string; b: string; corr: number }[];
    leastCorrelated: { a: string; b: string; corr: number }[];
  };
}

function buildClusters(
  tickers: string[],
  matrix: number[][],
  threshold = 0.5
): { members: string[]; avgIntraCorr: number }[] {
  const n = tickers.length;
  let clusters: number[][] = tickers.map((_, i) => [i]);

  const dist = (a: number[], b: number[]) => {
    let sum = 0;
    for (const i of a) for (const j of b) sum += 1 - matrix[i][j];
    return sum / (a.length * b.length);
  };

  while (clusters.length > 1) {
    let minDist = Infinity, mergeI = 0, mergeJ = 1;
    for (let i = 0; i < clusters.length; i++) {
      for (let j = i + 1; j < clusters.length; j++) {
        const d = dist(clusters[i], clusters[j]);
        if (d < minDist) { minDist = d; mergeI = i; mergeJ = j; }
      }
    }
    if (minDist > 1 - threshold) break;
    const merged = [...clusters[mergeI], ...clusters[mergeJ]];
    clusters = clusters.filter((_, idx) => idx !== mergeI && idx !== mergeJ);
    clusters.push(merged);
  }

  return clusters.map((members) => {
    let sum = 0, count = 0;
    for (let i = 0; i < members.length; i++)
      for (let j = i + 1; j < members.length; j++) {
        sum += matrix[members[i]][members[j]];
        count++;
      }
    return {
      members: members.map((i) => tickers[i]),
      avgIntraCorr: count > 0 ? sum / count : 1,
    };
  }).sort((a, b) => b.members.length - a.members.length);
}

export async function fetchCorrelationForReport(
  tickers: string[],
  period = "6m"
): Promise<CorrelationReportData | null> {
  if (tickers.length < 2) return null;

  try {
    const { data, error } = await supabase.functions.invoke("portfolio-correlation", {
      body: { tickers: tickers.slice(0, 30), period },
    });
    if (error || data?.error) return null;

    const resultTickers: string[] = data.tickers;
    const matrix: number[][] = data.matrix;

    // Build insights
    const pairs: { a: string; b: string; corr: number }[] = [];
    for (let i = 0; i < resultTickers.length; i++)
      for (let j = i + 1; j < resultTickers.length; j++)
        pairs.push({ a: resultTickers[i], b: resultTickers[j], corr: matrix[i][j] });

    pairs.sort((a, b) => b.corr - a.corr);
    const avgCorr = pairs.length > 0 ? pairs.reduce((s, p) => s + p.corr, 0) / pairs.length : 0;

    const clusters = buildClusters(resultTickers, matrix);

    return {
      tickers: resultTickers,
      matrix,
      period: data.period || period,
      dataPoints: data.dataPoints || 0,
      clusters,
      sectorMap: data.sectorMap || undefined,
      insights: {
        avgCorr,
        mostCorrelated: pairs.slice(0, 3),
        leastCorrelated: [...pairs].sort((a, b) => a.corr - b.corr).slice(0, 3),
      },
    };
  } catch {
    return null;
  }
}
